import { createReducer, on } from '@ngrx/store';
import { ManagerFeedbackActions } from './manager-feedback.actions';
import {
  FeedbackDashboardItemSchema,
  FeedbackDetailSchema,
  FeedbackListParams
} from '@app/core/api/manager-feedback';

// =============================================================================
// State Interface
// =============================================================================

export interface ManagerFeedbackState {
  list: FeedbackDashboardItemSchema[];
  pagination: {
    total: number;
    page: number;
    size: number;
    pages: number;
  };
  filters: FeedbackListParams;
  selectedFeedback: FeedbackDetailSchema | null;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

// =============================================================================
// Initial State
// =============================================================================

export const initialManagerFeedbackState: ManagerFeedbackState = {
  list: [],
  pagination: {
    total: 0,
    page: 1,
    size: 10,
    pages: 0
  },
  filters: {},
  selectedFeedback: null,
  loading: false,
  detailLoading: false,
  error: null
};

// =============================================================================
// Reducer
// =============================================================================

export const managerFeedbackReducer = createReducer(
  initialManagerFeedbackState,

  // Load feedback list
  on(ManagerFeedbackActions.loadFeedbackList, (state): ManagerFeedbackState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(ManagerFeedbackActions.loadFeedbackListSuccess, (state, { items, total, page, size, pages }): ManagerFeedbackState => ({
    ...state,
    list: items,
    pagination: { total, page, size, pages },
    loading: false,
    error: null
  })),
  on(ManagerFeedbackActions.loadFeedbackListFailure, (state, { error }): ManagerFeedbackState => ({
    ...state,
    loading: false,
    error
  })),

  // Load feedback detail
  on(ManagerFeedbackActions.loadFeedbackDetail, (state): ManagerFeedbackState => ({
    ...state,
    detailLoading: true,
    error: null
  })),
  on(ManagerFeedbackActions.loadFeedbackDetailSuccess, (state, { feedback }): ManagerFeedbackState => ({
    ...state,
    selectedFeedback: feedback,
    detailLoading: false,
    error: null
  })),
  on(ManagerFeedbackActions.loadFeedbackDetailFailure, (state, { error }): ManagerFeedbackState => ({
    ...state,
    detailLoading: false,
    error
  })),

  // Update status
  on(ManagerFeedbackActions.updateStatus, (state): ManagerFeedbackState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(ManagerFeedbackActions.updateStatusSuccess, (state, { feedback }): ManagerFeedbackState => ({
    ...state,
    loading: false,
    // Update the item in the list
    list: state.list.map(item => item.id === feedback.id ? feedback : item),
    // Update selectedFeedback status if it matches
    selectedFeedback: state.selectedFeedback?.id === feedback.id
      ? { ...state.selectedFeedback, status: feedback.status }
      : state.selectedFeedback
  })),
  on(ManagerFeedbackActions.updateStatusFailure, (state, { error }): ManagerFeedbackState => ({
    ...state,
    loading: false,
    error
  })),

  // Add note
  on(ManagerFeedbackActions.addNote, (state): ManagerFeedbackState => ({
    ...state,
    error: null
  })),
  on(ManagerFeedbackActions.addNoteSuccess, (state, { note }): ManagerFeedbackState => ({
    ...state,
    selectedFeedback: state.selectedFeedback
      ? {
          ...state.selectedFeedback,
          notes: [...state.selectedFeedback.notes, note],
          notes_count: state.selectedFeedback.notes_count + 1
        }
      : null,
    // Also update notes_count in the list
    list: state.list.map(item =>
      item.id === state.selectedFeedback?.id
        ? { ...item, notes_count: item.notes_count + 1 }
        : item
    )
  })),
  on(ManagerFeedbackActions.addNoteFailure, (state, { error }): ManagerFeedbackState => ({
    ...state,
    error
  })),

  // Send email
  on(ManagerFeedbackActions.sendEmail, (state): ManagerFeedbackState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(ManagerFeedbackActions.sendEmailSuccess, (state): ManagerFeedbackState => ({
    ...state,
    loading: false
  })),
  on(ManagerFeedbackActions.sendEmailFailure, (state, { error }): ManagerFeedbackState => ({
    ...state,
    loading: false,
    error
  })),

  // Delete feedback
  on(ManagerFeedbackActions.deleteFeedback, (state): ManagerFeedbackState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(ManagerFeedbackActions.deleteFeedbackSuccess, (state, { id }): ManagerFeedbackState => ({
    ...state,
    loading: false,
    list: state.list.filter(item => item.id !== id),
    selectedFeedback: state.selectedFeedback?.id === id ? null : state.selectedFeedback,
    pagination: {
      ...state.pagination,
      total: state.pagination.total - 1
    }
  })),
  on(ManagerFeedbackActions.deleteFeedbackFailure, (state, { error }): ManagerFeedbackState => ({
    ...state,
    loading: false,
    error
  })),

  // Delete note
  on(ManagerFeedbackActions.deleteNote, (state): ManagerFeedbackState => ({
    ...state,
    error: null
  })),
  on(ManagerFeedbackActions.deleteNoteSuccess, (state, { feedbackId, noteId }): ManagerFeedbackState => ({
    ...state,
    selectedFeedback: state.selectedFeedback?.id === feedbackId
      ? {
          ...state.selectedFeedback,
          notes: state.selectedFeedback.notes.filter(n => n.id !== noteId),
          notes_count: state.selectedFeedback.notes_count - 1
        }
      : state.selectedFeedback,
    list: state.list.map(item =>
      item.id === feedbackId
        ? { ...item, notes_count: item.notes_count - 1 }
        : item
    )
  })),
  on(ManagerFeedbackActions.deleteNoteFailure, (state, { error }): ManagerFeedbackState => ({
    ...state,
    error
  })),

  // Set filters
  on(ManagerFeedbackActions.setFilters, (state, { filters }): ManagerFeedbackState => ({
    ...state,
    filters: { ...state.filters, ...filters }
  })),

  // Clear selected feedback
  on(ManagerFeedbackActions.clearSelectedFeedback, (state): ManagerFeedbackState => ({
    ...state,
    selectedFeedback: null
  })),

  // Clear errors
  on(ManagerFeedbackActions.clearErrors, (state): ManagerFeedbackState => ({
    ...state,
    error: null
  }))
);
